import * as THREE from 'three';
import { GameObject } from '../objects/GameObject';
import { Config } from '../core/Config';

// Rectangle boundary class for quadtree
export class Boundary {
  constructor(
    public x: number, 
    public z: number, 
    public width: number, 
    public height: number
  ) {}

  // Check if this boundary contains a point
  public contains(position: THREE.Vector3): boolean {
    return (
      position.x >= this.x - this.width &&
      position.x < this.x + this.width &&
      position.z >= this.z - this.height &&
      position.z < this.z + this.height
    );
  }

  // Check if this boundary intersects with another boundary
  public intersects(range: Boundary): boolean {
    return !(
      range.x - range.width > this.x + this.width ||
      range.x + range.width < this.x - this.width ||
      range.z - range.height > this.z + this.height ||
      range.z + range.height < this.z - this.height
    );
  }
}

// Quadtree class
export class Quadtree {
  private boundary: Boundary;
  private capacity: number;
  private objects: GameObject[] = [];
  private divided: boolean = false;

  // Child quadtrees
  private northwest: Quadtree | null = null;
  private northeast: Quadtree | null = null;
  private southwest: Quadtree | null = null;
  private southeast: Quadtree | null = null;

  // Debug visualization
  private debugMesh: THREE.LineSegments | null = null;
  
  // Track which node contains which object for efficient removal
  // This is a static map shared across all quadtree nodes
  private static objectNodeMap: Map<string, Quadtree> = new Map();
  
  // For node identification in debugging
  private id: string;
  private static nodeCounter: number = 0;

  // Update the type to be more specific for THREE.js LineSegments
  private static debugMeshes: THREE.LineSegments[] = [];
  
  constructor(boundary: Boundary, capacity: number = Config.QUADTREE_CAPACITY) {
    this.boundary = boundary;
    this.capacity = capacity;
    this.id = `node_${Quadtree.nodeCounter++}`;
  }

  // Insert an object into the quadtree
  public insert(object: GameObject): boolean {
    // Object doesn't fit in this quad
    const objectPosition = object.getPosition();
    if (!this.boundary.contains(objectPosition)) {
      return false;
    }

    // If there's room, add the object here
    if (this.objects.length < this.capacity && !this.divided) {
      this.objects.push(object);
      
      // Store which node contains this object
      Quadtree.objectNodeMap.set(object.getId(), this);
      return true;
    }

    // Otherwise, subdivide and then add the object to whichever node will accept it
    if (!this.divided) {
      this.subdivide();
    }

    if (
      this.northwest!.insert(object) ||
      this.northeast!.insert(object) ||
      this.southwest!.insert(object) ||
      this.southeast!.insert(object)
    ) {
      return true;
    }

    // If we got here, something went wrong
    console.error("Quadtree insertion failed - this shouldn't happen", object);
    return false;
  }

  /**
   * Remove an object from the quadtree by its ID
   * @param objectId ID of the object to remove
   * @returns true if object was found and removed, false otherwise
   */
  public remove(objectId: string): boolean {
    // Check if we know which node contains this object
    const node = Quadtree.objectNodeMap.get(objectId);
    
    if (node) {
      // Remove from the known node
      const result = node.removeFromNode(objectId);
      if (result) {
        Quadtree.objectNodeMap.delete(objectId);
      }
      return result;
    }
    
    // If we don't know the node, we need to search the tree
    return this.removeFromNode(objectId);
  }
  
  /**
   * Remove an object from this specific node
   * @param objectId ID of the object to remove
   * @returns true if object was found and removed, false otherwise
   */
  private removeFromNode(objectId: string): boolean {
    // Check objects at this level
    const index = this.objects.findIndex(obj => obj.getId() === objectId);
    if (index !== -1) {
      // Remove object from this node
      this.objects.splice(index, 1);
      Quadtree.objectNodeMap.delete(objectId);
      return true;
    }
    
    // If this node is divided, check children
    if (this.divided) {
      return (
        this.northwest!.removeFromNode(objectId) ||
        this.northeast!.removeFromNode(objectId) ||
        this.southwest!.removeFromNode(objectId) ||
        this.southeast!.removeFromNode(objectId)
      );
    }
    
    // Object not found in this subtree
    return false;
  }
  
  /**
   * Update an object's position in the quadtree
   * This efficiently removes and reinserts only when necessary
   * @param object The object to update
   * @returns true if update was successful
   */
  public update(object: GameObject): boolean {
    const objectId = object.getId();
    const currentPosition = object.getPosition();
    const currentNode = Quadtree.objectNodeMap.get(objectId);
    
    // If we don't know which node contains the object, do a full search
    if (!currentNode) {
      // Try to remove from the whole tree
      this.remove(objectId);
      // Then reinsert
      return this.insert(object);
    }
    
    // If the object is still in the boundary of its current node and
    // that node isn't subdivided, we don't need to do anything
    if (currentNode.boundary.contains(currentPosition) && 
        currentNode.objects.includes(object) && 
        !currentNode.divided) {
      return true;
    }
    
    // Otherwise, remove and reinsert
    this.remove(objectId);
    return this.insert(object);
  }

  // Create four children that fully divide this quad into four quads of equal area
  private subdivide(): void {
    const x = this.boundary.x;
    const z = this.boundary.z;
    const w = this.boundary.width / 2;
    const h = this.boundary.height / 2;

    const nw = new Boundary(x - w, z - h, w, h);
    const ne = new Boundary(x + w, z - h, w, h);
    const sw = new Boundary(x - w, z + h, w, h);
    const se = new Boundary(x + w, z + h, w, h);

    this.northwest = new Quadtree(nw, this.capacity);
    this.northeast = new Quadtree(ne, this.capacity);
    this.southwest = new Quadtree(sw, this.capacity);
    this.southeast = new Quadtree(se, this.capacity);

    // Move existing objects into children
    for (let i = this.objects.length - 1; i >= 0; i--) {
      const object = this.objects[i];
      if (
        this.northwest.insert(object) ||
        this.northeast.insert(object) ||
        this.southwest.insert(object) ||
        this.southeast.insert(object)
      ) {
        this.objects.splice(i, 1);
      }
    }

    this.divided = true;
  }

  // Query objects in a given range
  public query(range: Boundary, found: GameObject[] = []): GameObject[] {
    // If the range doesn't intersect this quadtree, don't check further
    if (!this.boundary.intersects(range)) {
      return found;
    }

    // Check objects at this level
    for (const object of this.objects) {
      const position = object.getPosition();
      if (range.contains(position)) {
        found.push(object);
      }
    }

    // Recursively check children if this quadtree is divided
    if (this.divided) {
      this.northwest!.query(range, found);
      this.northeast!.query(range, found);
      this.southwest!.query(range, found);
      this.southeast!.query(range, found);
    }

    return found;
  }

  // Find objects near a point within a radius
  public queryRadius(center: THREE.Vector3, radius: number): GameObject[] {
    // First, create a boundary that encompasses the circle
    const boundary = new Boundary(
      center.x, 
      center.z, 
      radius, 
      radius
    );
    
    // Get all objects within the square boundary
    const candidates = this.query(boundary);
    
    // Then filter to just those within the radius
    return candidates.filter(obj => {
      const pos = obj.getPosition();
      const dx = pos.x - center.x;
      const dz = pos.z - center.z;
      const distSquared = dx * dx + dz * dz;
      return distSquared <= radius * radius;
    });
  }

  /**
   * Clear all objects and reset the quadtree
   */
  public clear(): void {
    // Clear all objects from this node
    this.objects = [];
    
    // Clear all children if this node is divided
    if (this.divided) {
      this.northwest!.clear();
      this.northeast!.clear();
      this.southwest!.clear();
      this.southeast!.clear();
      
      this.northwest = null;
      this.northeast = null;
      this.southwest = null;
      this.southeast = null;
      this.divided = false;
    }
    
    // Clear the debug mesh if it exists
    if (this.debugMesh) {
      // Just nullify the reference since the scene might not be available
      this.debugMesh = null;
    }
  }
  
  /**
   * Clear all object-to-node mappings
   * Call this when completely resetting the quadtree system
   */
  public static clearObjectNodeMap(): void {
    Quadtree.objectNodeMap.clear();
    Quadtree.nodeCounter = 0;
  }
  
  /**
   * Get the node that contains an object
   * @param objectId ID of the object
   * @returns The quadtree node containing the object, or null if not found
   */
  public static getContainingNode(objectId: string): Quadtree | undefined {
    return Quadtree.objectNodeMap.get(objectId);
  }
  
  /**
   * Remove an object from the entire quadtree
   * @param objectId ID of the object to remove
   * @returns true if the object was found and removed
   */
  public static removeObject(objectId: string): boolean {
    const node = Quadtree.objectNodeMap.get(objectId);
    if (node) {
      const result = node.removeFromNode(objectId);
      if (result) {
        Quadtree.objectNodeMap.delete(objectId);
      }
      return result;
    }
    return false;
  }

  // Create or update visualization mesh for debugging
  public createDebugMesh(scene: THREE.Scene, depth: number = 0, maxDepth: number = Config.QUADTREE_DEBUG_MAX_DEPTH): void {
    // Remove old debug mesh from scene if it exists
    if (this.debugMesh && this.debugMesh.parent) {
      scene.remove(this.debugMesh);
      
      // Remove from static tracking array
      const index = Quadtree.debugMeshes.indexOf(this.debugMesh);
      if (index !== -1) {
        Quadtree.debugMeshes.splice(index, 1);
      }
    }
    
    // Properly dispose of geometry and materials
    if (this.debugMesh) {
      if (this.debugMesh.geometry) {
        this.debugMesh.geometry.dispose();
      }
      
      if (this.debugMesh.material) {
        if (Array.isArray(this.debugMesh.material)) {
          this.debugMesh.material.forEach(material => material.dispose());
        } else {
          this.debugMesh.material.dispose();
        }
      }
      
      this.debugMesh = null;
    }

    // Only visualize up to max depth
    if (depth > maxDepth) return;

    // Create a square wireframe to represent this quadtree node
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineBasicMaterial({ 
      color: this.getDebugColor(depth),
      transparent: true,
      opacity: 0.7
    });

    // Create square at boundary + slight y offset to avoid z-fighting with terrain
    const x = this.boundary.x;
    const z = this.boundary.z;
    const w = this.boundary.width;
    const h = this.boundary.height;
    const y = 0.05; // Slight offset from ground

    // Create vertices for a square
    const vertices = new Float32Array([
      x - w, y, z - h,   // Bottom left
      x + w, y, z - h,   // Bottom right
      x + w, y, z + h,   // Top right
      x - w, y, z + h,   // Top left
      x - w, y, z - h    // Back to bottom left to close the square
    ]);

    // Add vertices to geometry
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    
    // Create mesh and add to scene
    this.debugMesh = new THREE.LineSegments(geometry, material);
    scene.add(this.debugMesh);
    
    // Add to static tracking array for global cleanup
    Quadtree.debugMeshes.push(this.debugMesh);

    // Recursively create debug meshes for children
    if (this.divided) {
      this.northwest!.createDebugMesh(scene, depth + 1, maxDepth);
      this.northeast!.createDebugMesh(scene, depth + 1, maxDepth);
      this.southwest!.createDebugMesh(scene, depth + 1, maxDepth);
      this.southeast!.createDebugMesh(scene, depth + 1, maxDepth);
    }
  }

  /**
   * Remove debug visualization mesh from scene
   * @param scene The Three.js scene
   */
  public removeDebugMesh(scene: THREE.Scene): void {
    // Remove this node's debug mesh
    if (this.debugMesh) {
      if (scene) {
        scene.remove(this.debugMesh);
      }
      
      // Remove from static tracking array
      const index = Quadtree.debugMeshes.indexOf(this.debugMesh);
      if (index !== -1) {
        Quadtree.debugMeshes.splice(index, 1);
      }
      
      // Properly dispose of geometry and materials
      if (this.debugMesh.geometry) {
        this.debugMesh.geometry.dispose();
      }
      
      if (this.debugMesh.material) {
        if (Array.isArray(this.debugMesh.material)) {
          this.debugMesh.material.forEach(material => material.dispose());
        } else {
          (this.debugMesh.material as THREE.Material).dispose();
        }
      }
      
      this.debugMesh = null;
    }
    
    // Recursively remove debug meshes from children
    if (this.divided) {
      this.northwest!.removeDebugMesh(scene);
      this.northeast!.removeDebugMesh(scene);
      this.southwest!.removeDebugMesh(scene);
      this.southeast!.removeDebugMesh(scene);
    }
  }
  
  /**
   * Static method to clean up all debug meshes
   * Call this when toggling debug visualization off or during scene changes
   * @param scene The Three.js scene
   */
  public static clearAllDebugMeshes(scene: THREE.Scene): void {
    // Make a copy of the array to avoid modification during iteration
    const meshesToRemove = [...Quadtree.debugMeshes];
    
    for (const mesh of meshesToRemove) {
      if (mesh && scene) {
        scene.remove(mesh);
        
        // Properly dispose of geometry and materials
        if (mesh.geometry) {
          mesh.geometry.dispose();
        }
        
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((material: THREE.Material) => material.dispose());
          } else {
            (mesh.material as THREE.Material).dispose();
          }
        }
      }
    }
    
    // Clear the array
    Quadtree.debugMeshes = [];
  }

  // Get a color based on depth
  private getDebugColor(depth: number): number {
    // Different colors for different depths
    const colors = [
      0xff0000, // Red
      0x00ff00, // Green
      0x0000ff, // Blue
      0xffff00, // Yellow
      0xff00ff, // Magenta
      0x00ffff  // Cyan
    ];
    return colors[depth % colors.length];
  }

  // Get statistics about this quadtree (for debugging)
  public getStats(): { nodes: number, objects: number, maxDepth: number } {
    let nodes = 1;
    let objects = this.objects.length;
    let maxDepth = 0;

    if (this.divided) {
      const nwStats = this.northwest!.getStats();
      const neStats = this.northeast!.getStats();
      const swStats = this.southwest!.getStats();
      const seStats = this.southeast!.getStats();

      nodes += nwStats.nodes + neStats.nodes + swStats.nodes + seStats.nodes;
      objects += nwStats.objects + neStats.objects + swStats.objects + seStats.objects;
      maxDepth = 1 + Math.max(
        nwStats.maxDepth, neStats.maxDepth, swStats.maxDepth, seStats.maxDepth
      );
    }

    return { nodes, objects, maxDepth };
  }
}
